import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { OverlayTrigger, Modal } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import BllinkUtils from '../../../common/utils/utils';
import arrow from '../../../images/newDesign/arrow_down.svg';
import check from '../../../images/newDesign/purple_check.svg';
import { constants } from "../../../common/constants/constants";
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { CurrencyValue } from '../../../common/currencyValue';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { AddExpenseModalStyled } from '../../expenses/set_expense_process/SetExpense.styled';
import AddPaymentMethodOneTime from '../../expenses/set_expense_process/AddPaymentMethodOneTime';
import Confirmation from '../../expenses/set_expense_process/Confirmation';
import { StyledPopover } from '../debt_list/debtList.styled';
import DeleteModal from '../apartment_cashflow_table/DeleteModal';
import { StyledExpensesCard, StyledPopoverBody, StyledExpensesTooltip } from './MonthlyExpensesNew.styled';

const FilterUtils = BllinkUtils.Filter;

export const InnerCardWrapper = ({ header, value }) => {
    return (
        <div className='expenses-info-inner-wrapper'>
            <div className='header'>{header}:</div>
            <div className='value'>{value}</div>
        </div>
    )
};

const ExpensesCard = ({ expense, filter, callback, loadExpenses, titleData, buildingID }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();
    const ltr = useSelector(state => state.common.ltrEnabled);

    const [showSecondRow, setShowSecondRow] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [currentPaymentMethod, setCurrentPaymentMethod] = useState(constants.managerPaymentsOptions.bankCharge);
    const [addPaymentMethodPayload, setAddPaymentMethodPayload] = useState();
    const [confirmationDate, setConfirmationDate] = useState(expense.chargeDate);
    const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
    const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(true);
    const [choosePaymentMethod, setChoosePaymentMethod] = useState(true);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [deletePending, setDeletePending] = useState('one');

    const date = BllinkUtils.BllinkTime.parseDate(expense.chargeDate);
    const amount = expense.amount;
    const paidValue = expense.paid;
    const currency = expense.currency && t(`currency.${expense.currency}`);
    const vendor = expense.vendorName;
    const paymentType = expense.expenseTypeKey;
    const paymentTypeText = paymentType && t(`expenses.expenseTypes.${paymentType}`);
    const frequency = expense.isRecurring === 'yes' ? t('reports.recurring') : t('reports.oneTime')

    const bankReconsileNumber = expense?.expenseState;
    const bankReconsileKey = constants.PaymentPlanDetailsStatus[bankReconsileNumber];
    const bankReconsile = bankReconsileKey ? t(`statuses.${bankReconsileKey}`) : null;

    const isExpensePaymentSettled = expense.method !== constants.APIContract.paymentMethodsKeys.notSet;
    const chequeNumber = isExpensePaymentSettled ? expense?.expenseID : null;
    const paymentMethodKey = isExpensePaymentSettled ? expense.method : null;
    const paymentMethod = paymentMethodKey ? t(`buildings.${paymentMethodKey}`) : null;

    const note = expense?.comment;
    const cartID = expense?.cartID;
    const planID = expense?.planID;
    const showItem = FilterUtils.filterItems([date, amount, vendor, paymentTypeText, frequency, bankReconsile, paymentMethod, chequeNumber, note], filter);

    const parsedPaidValue = BllinkUtils.Strings.numberWithCommas(paidValue);

    const statusName = useMemo(() => {
        let result = ''
        if (expense?.isOnline) {
            switch (expense.expenseState) {
                case 13:
                case 1:
                    result = t('reports.status.awaiting_withdrawal');
                    break;
                case 12:
                    result = t('reports.status.pending_approval');
                    break;
                case 2:
                    // https://bllink.atlassian.net/browse/SSB-828 moving to new method
                    // of marking the vendor payments as transferred
                    if (expense.isTransferredToVendor){
                        result = t('reports.status.approved');
                    }else{
                        result = t('reports.status.pulled_from_building');
                    }

                    break;
                case 9:
                    result = t('reports.status.failed');
                    break;
                default:
                    result = '';
            }
        } else {
            result = t(`reports.${expense?.isPaid ? 'apartment.paid' : 'unpaid'}`);
        }

        return result;
    }, [expense])

    useEffect(() => callback({date, amount, paid: paidValue, currency, vendor, paymentTypeText, frequency, bankReconsile, paymentMethod, chequeNumber, note, cartID, status: statusName}), []);

    const openCloseCard = () => setShowSecondRow(!showSecondRow);

    const handleRadioChange = e => {
        setDeletePending(e.target.value);
    };

    const redirectToConfirmationPage = () => window.open(`/status/${cartID}`, '_blank');

    const onFileDownload = async () => {
        const response = await client.managers.expenses.getExpenseFileInfo(buildingID, planID);
        if (response?.exists){
            window.open(response.fileInfo.url, '_blank');
        } else {
            NotificationManager.error(t('reports.file_not_found'));
        }
        setShowPopover(false);
    }

    const deleteExpense = () => {
        setShowPopover(false);
        setShowDeleteModal(true);
    };

    const onDeleteModalHide = () => setShowDeleteModal(false);

    const deletePayment = async () => {
        dispatch(showLoader());

        const query = `deleteAllPending=${deletePending === 'all'}&deleteAllNextPending=${deletePending === 'allNext'}`;
        const response = await client.post(`/managers/delete-external-payments/${expense?.paymentID}?${query}`)

        await loadExpenses();

        const updated = response?.paymentResult?.updated;
        if (updated && updated > 0) NotificationManager.success(`${updated} ${t('reports.delete.notificationSuccess')}`, '', 5000);
        else if (updated && updated === 0) NotificationManager.warning(t('reports.delete.notificationNoUpdates'), '', 5000);
        else NotificationManager.error(t('reports.delete.notificationFailure'), '', 5000);

        onDeleteModalHide();

        dispatch(hideLoader());
    };

    const addPaymentDetails = async () => {
        setShowPopover(false);
        setShowAddExpenseModal(true);
        setShowAddPaymentMethod(true);
    };

    const onHide = () => {
        setShowAddExpenseModal(false)
        setShowAddPaymentMethod(false);
        setChoosePaymentMethod(true);
        setShowConfirmation(false);
        setCurrentPaymentMethod(constants.managerPaymentsOptions.bankCharge);
    };

    const onPaymentMethodChange = method => setCurrentPaymentMethod(method);

    return (
        showItem && <StyledExpensesCard ltr={ltr} showSecondRow={showSecondRow}>
            {expense?.isPaid && <img src={check} className='paid-check' />}
            <div className='first-row'>
                <div>{date}</div>
                <CurrencyValue currency={expense?.currency} notInCents={true}>{parsedPaidValue}</CurrencyValue>
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <StyledExpensesTooltip {...props}>
                            {vendor}
                        </StyledExpensesTooltip>
                    )}
                >
                    <div className='vendor'>{vendor}</div>
                </OverlayTrigger>
                <div>{paymentTypeText}</div>
                <div>{frequency}</div>
                <div>{paymentMethod}</div>
                <div>{chequeNumber}</div>
            </div>
            {showSecondRow && (
                <div className='second-row'>
                    <div className='expenses-info-inner-wrapper'>
                        <div className='header'>{t('expenses.expense_status')}:</div>
                        <div className='value paid-text'>{statusName}</div>
                    </div>
                    <InnerCardWrapper
                        header={t('reports.bank_reconsile')}
                        value={bankReconsile}
                    />
                    <InnerCardWrapper
                        header={t('reports.note')}
                        value={note}
                    />
                </div>
            )}
            <div className='open-close-btn' onClick={openCloseCard}><img src={arrow} /></div>

            <OverlayTrigger
                rootClose
                show={showPopover}
                onToggle={() => setShowPopover(!showPopover)}
                trigger="click"
                placement="right"
                overlay={(
                    <StyledPopover>
                        <StyledPopoverBody>
                            <div className='link' onClick={redirectToConfirmationPage}>{t('reports.open_confirmation')}</div>
                            <div className={`link ${expense?.isEditable ? '' : 'disabled'}`} onClick={deleteExpense}>{t('reports.delete_expense')}</div>
                            {/* TODO: enable when there is no payment method (after backend for adding details is ready) */}
                            <div className={`link ${paymentMethod ? 'disabled' : ''}`} onClick={addPaymentDetails}>{t('reports.add_payment_details')}</div>
                            <div className='link' onClick={onFileDownload}>{t('reports.download_files')}</div>
                        </StyledPopoverBody>
                    </StyledPopover>
                )
            }>
                <div className='tool'>...</div>
            </OverlayTrigger>

            <DeleteModal
                show={showDeleteModal}
                onHide={onDeleteModalHide}
                onDeleteClick={deletePayment}
                rowToDelete={expense}
                currentRadio={deletePending}
                onRadioChange={handleRadioChange}
            />

            <AddExpenseModalStyled
                show={showAddExpenseModal}
                onHide={onHide}
                centered
                size='lg'
                ltr={ltr}
            >
                <span
                    className='modal-close'
                    onClick={onHide}
                >
                    &#215;
                </span>
                <div className='set-expense-title'>
                    {showAddPaymentMethod && !showConfirmation && t('expenses.set_expenses.pay_with_bllink_or_record_offline')}
                    {showConfirmation && (
                        <>
                            {t('expenses.set_expenses.confirmation_title')}
                            {titleData.translationKey && (
                                <span className='building-title'>{t('payments.' + titleData.translationKey, titleData)}</span>
                            )}
                        </>
                    )}
            </div>

            <Modal.Body>
                {showAddPaymentMethod && !showConfirmation && (
                    <AddPaymentMethodOneTime
                        amount={amount}
                        currency={expense.currency}
                        vendorName={vendor}
                        choosePaymentMethod={choosePaymentMethod}
                        setChoosePaymentMethod={setChoosePaymentMethod}
                        paymentMethod={currentPaymentMethod}
                        onMethodChange={onPaymentMethodChange}
                        vendorID={expense.vendorExternalID}
                        buildingID={buildingID}
                        setShowConfirmation={setShowConfirmation}
                        date={confirmationDate}
                        setConfirmationDate={setConfirmationDate}
                        setShowAddPaymentMethod={setShowAddPaymentMethod}
                        setAddPaymentMethodPayload={setAddPaymentMethodPayload}
                        showFromReport
                    />
                )}

                {showConfirmation && (
                    <Confirmation
                        vendorName={vendor}
                        amount={amount}
                        paymentMethod={currentPaymentMethod}
                        currency={expense.currency}
                        onHide={onHide}
                        date={confirmationDate}
                        addPaymentMethodPayload={addPaymentMethodPayload}
                        setShowConfirmation={setShowConfirmation}
                        setShowAddPaymentMethod={setShowAddPaymentMethod}
                        setChoosePaymentMethod={setChoosePaymentMethod}
                        paymentPlanID={planID}
                        showFromReport
                        loadExpenses={loadExpenses}
                    />
                )}
            </Modal.Body>
            </AddExpenseModalStyled>
        </StyledExpensesCard>
    );
};

export default ExpensesCard;
